import type { AuditObjectAuditSummary, AuditScoreType } from "@ots-frontend/ots-data";
import type { ReactNode } from "react";
import { DescriptionItem, DescriptionList } from "../components/DescriptionList";
import { OtsConfidenceRing } from "../components/OtsConfidenceRing";
import { OtsTooltip } from "../components/OtsTooltip";
import { formatAuditDateTime } from "./audit-utils";

export const auditScoreLabels: Record<AuditScoreType, string> = {
  FIELD_VALIDATION_CONFIDENCE: "Field validation",
  BUSINESS_VALIDATION_CONFIDENCE: "Business validation",
};

/**
 * A confidence as a 0–1 ratio. The audit service reports some as fractions and some as percentages, so,
 * as WaterGo does, anything above 1 is read as a percentage.
 */
export const toConfidenceRatio = (value?: number | null): number | undefined => {
  if (value == null || !Number.isFinite(value)) return undefined;
  return Math.min(Math.max(value > 1 ? value / 100 : value, 0), 1);
};

export const formatConfidence = (value?: number | null) => {
  const ratio = toConfidenceRatio(value);
  return ratio == null ? "not scored" : `${Math.round(ratio * 1000) / 10}%`;
};

/** The facts of an audit summary as label/value pairs, shared by the ring's tooltip and the inspector. */
export const auditSummaryFacts = (audit: AuditObjectAuditSummary, timeZone?: string): [label: string, value: ReactNode][] => {
  const severities = Object.entries(audit.findingCountsBySeverity).filter(([, count]) => count)
    .map(([severity, count]) => `${count} ${severity.toLowerCase()}`).join(", ");
  return [
    ...(Object.keys(auditScoreLabels) as AuditScoreType[]).map((type): [string, ReactNode] => [auditScoreLabels[type], formatConfidence(audit.confidences[type])]),
    ["Findings", severities ? `${audit.findingCount} (${severities})` : audit.findingCount],
    ["Blocking", audit.blockingFindingCount],
    ["Last run", audit.latestRunCompletedAtDttm != null ? formatAuditDateTime(audit.latestRunCompletedAtDttm, timeZone) : "—"],
  ];
};

export type AuditScoreRingProps = {
  /** Missing while audits are still loading, or when the ticket has none. */
  audit?: AuditObjectAuditSummary;
  /** Which confidence the ring shows. */
  scoreType: AuditScoreType;
  /** The ring stands for the weakest ticket of a driver or shift, not one ticket. */
  aggregate?: boolean;
  timeZone?: string;
  size?: number;
  /** Makes the ring a button, e.g. to open the ticket's audit. */
  onClick?: () => void;
};

/** A ticket's latest audit as a confidence ring: red when a finding blocks it, with the summary on hover. */
export const AuditScoreRing = ({ audit, scoreType, aggregate = false, timeZone, size, onClick }: AuditScoreRingProps) => {
  const audited = audit?.hasAuditRun;
  const value = audited ? toConfidenceRatio(audit.confidences[scoreType]) : undefined;
  const blocked = (audit?.blockingFindingCount ?? 0) > 0;
  const label = !audit ? "Audit summary not loaded"
    : !audited ? "Not audited yet"
      : `${aggregate ? "Lowest " : ""}${auditScoreLabels[scoreType].toLowerCase()} confidence ${formatConfidence(value)}${blocked ? ", blocked" : ""}`;

  return (
    <OtsTooltip
      density="compact"
      content={audited ? <>
        <p className="mb-1 font-semibold">{aggregate ? "Weakest ticket's audit" : "Latest audit"} · {auditScoreLabels[scoreType]} shown</p>
        <DescriptionList layout="inline" tone="inherit" compact>
          {auditSummaryFacts(audit, timeZone).map(([fact, text]) => <DescriptionItem key={fact} label={fact}>{text}</DescriptionItem>)}
        </DescriptionList>
      </> : label}>
      {onClick
        ? <button
          type="button"
          aria-label={`Open audit: ${label}`}
          onClick={(event) => {
            // The row behind it opens the inspector; this opens the audit.
            event.stopPropagation();
            onClick();
          }}
          className="inline-flex cursor-pointer rounded-full outline-none hover:ring-2 hover:ring-[var(--ots-primary)] focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]">
          <OtsConfidenceRing value={value} blocked={blocked} label={label} size={size}/>
        </button>
        : <span tabIndex={0} className="inline-flex rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]">
          <OtsConfidenceRing value={value} blocked={blocked} label={label} size={size}/>
        </span>}
    </OtsTooltip>
  );
};
